import React, { useState, useEffect } from "react";
import { Download, Monitor, Apple, CheckCircle, Shield, Zap, Clock, AlertCircle } from "lucide-react";
import { apiBaseUrl } from "services/config";
import { useTheme } from "context/ThemeContext";

const FEATURES = [
  { icon: Zap,          text: "Auto-starts on login — no manual setup" },
  { icon: Shield,       text: "Admin PIN protected — employees can't stop it" },
  { icon: Clock,        text: "Per-app time breakdown synced every 60s" },
  { icon: CheckCircle,  text: "Sleep & logoff aware — only counts active time" },
];

function DownloadCard({ os, icon: Icon, iconColor, iconBg, title, subtitle,
                        badge, steps, filename, onDownload, downloading, done }) {
  const { T } = useTheme();
  return (
    <div style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: 18,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      transition: "box-shadow .3s, background .25s, border-color .25s",
      boxShadow: T.shadow,
    }}>
      {/* Card header */}
      <div style={{
        padding: "28px 28px 20px",
        background: T.card2,
        borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:14 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16,
            background: iconBg,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <Icon size={28} style={{ color: iconColor }}/>
          </div>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ fontSize:20, fontWeight:800, color:T.text }}>{title}</div>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "2px 8px",
                borderRadius: 100, background: `${T.cyan}1F`,
                color: T.cyan, letterSpacing: "0.06em",
              }}>{badge}</span>
            </div>
            <div style={{ fontSize:13, color:T.muted, marginTop:3 }}>{subtitle}</div>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div style={{ padding:"20px 28px", flex:1 }}>
        <div style={{ fontSize:11, fontWeight:700, color:T.dim,
          textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:12 }}>
          Install in 3 steps
        </div>
        {steps.map((s, i) => (
          <div key={i} style={{ display:"flex", gap:10, marginBottom:10 }}>
            <div style={{
              width:22, height:22, borderRadius:"50%",
              background:`${T.cyan}1A`, border:`1px solid ${T.cyan}33`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:11, fontWeight:800, color:T.cyan, flexShrink:0, marginTop:1,
            }}>{i+1}</div>
            <div style={{ fontSize:13, color:T.muted, lineHeight:1.6 }}>{s}</div>
          </div>
        ))}
      </div>

      {/* Download button */}
      <div style={{ padding:"0 28px 28px" }}>
        <button
          onClick={() => onDownload(os, filename)}
          disabled={downloading}
          style={{
            width: "100%", padding: "14px 20px",
            borderRadius: 12, border: "none", cursor: downloading ? "wait" : "pointer",
            background: done
              ? `linear-gradient(135deg,${T.green},${T.cyan})`
              : downloading
              ? `${T.cyan}26`
              : `linear-gradient(135deg,${T.cyan},${T.purple})`,
            color: done ? T.bg : "#fff",
            fontFamily: "Outfit,sans-serif",
            fontSize: 14, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            transition: "all .25s",
            opacity: downloading ? 0.7 : 1,
            boxShadow: done || downloading ? "none" : `0 4px 20px ${T.cyan}4D`,
          }}
        >
          {done ? (
            <><CheckCircle size={16}/> Downloaded Successfully</>
          ) : downloading ? (
            <><div style={{ width:16, height:16, border:`2px solid ${T.cyan}4D`,
              borderTopColor:T.cyan, borderRadius:"50%",
              animation:"spin 0.8s linear infinite" }}/> Preparing Download…</>
          ) : (
            <><Download size={16}/> Download {title}</>
          )}
        </button>
        <div style={{ textAlign:"center", marginTop:8, fontSize:11, color:T.dim }}>
          {filename}
        </div>
      </div>
    </div>
  );
}

export default function DownloadAgent() {
  const { T } = useTheme();
  const [downloading, setDownloading] = useState({});
  const [done,        setDone]        = useState({});
  const [error,       setError]       = useState("");
  const [version,     setVersion]     = useState("2.0.0");

  useEffect(() => {
    // Fetch current version info from backend
    fetch(`${apiBaseUrl}/agent/info`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.version) setVersion(d.version); })
      .catch(() => {});
  }, []);

  const handleDownload = async (os, filename) => {
    setDownloading(p => ({ ...p, [os]: true }));
    setError("");
    try {
      const token = sessionStorage.getItem("platform_token") || "";
      const res = await fetch(`${apiBaseUrl}/agent/download/${os}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Download failed (${res.status})`);
      }

      // Trigger browser download
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setDone(p => ({ ...p, [os]: true }));
      setTimeout(() => setDone(p => ({ ...p, [os]: false })), 5000);
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloading(p => ({ ...p, [os]: false }));
    }
  };

  return (
    <div style={{ fontFamily:"'Outfit',sans-serif", color:T.text, maxWidth:900, margin:"0 auto" }}>

      {/* Page header */}
      <div style={{ marginBottom:32 }}>
        <div style={{
          display:"inline-flex", alignItems:"center", gap:6,
          padding:"4px 12px", borderRadius:100,
          background:`${T.cyan}1A`, border:`1px solid ${T.cyan}33`,
          fontSize:11, fontWeight:700, color:T.cyan,
          letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:12,
        }}>
          <div style={{ width:6, height:6, borderRadius:"50%",
            background:T.green, boxShadow:`0 0 6px ${T.green}`,
            animation:"pulse 1.8s infinite" }}/>
          Desktop Agent v{version}
        </div>
        <h2 style={{ fontSize:28, fontWeight:900, color:T.text, margin:"0 0 8px" }}>
          Download Hirality Agent
        </h2>
        <p style={{ fontSize:14, color:T.muted, margin:0, maxWidth:520, lineHeight:1.7 }}>
          Install on employee machines to automatically track active hours,
          app usage and productivity — synced directly to HRMS.
        </p>
      </div>

      {/* Feature pills */}
      <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:32 }}>
        {FEATURES.map(({ icon: Icon, text }, i) => (
          <div key={i} style={{
            display:"flex", alignItems:"center", gap:7,
            padding:"7px 14px", borderRadius:100,
            background:T.toggle_bg,
            border:`1px solid ${T.border}`,
            fontSize:12, color:T.muted,
          }}>
            <Icon size={13} style={{ color:T.cyan, flexShrink:0 }}/>
            {text}
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          display:"flex", alignItems:"center", gap:10,
          padding:"12px 18px", borderRadius:10, marginBottom:20,
          background:`${T.red}1A`, border:`1px solid ${T.red}40`,
          fontSize:13, color:T.red,
        }}>
          <AlertCircle size={16} style={{ flexShrink:0 }}/>
          {error}
          <button onClick={()=>setError("")}
            style={{ marginLeft:"auto", background:"none", border:"none",
              color:T.red, cursor:"pointer", fontSize:16 }}>✕</button>
        </div>
      )}

      {/* Download cards */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:28 }}>
        <DownloadCard
          os="mac"
          icon={Apple}
          iconColor={T.text}
          iconBg={T.toggle_bg}
          title="Hirality Agent"
          subtitle="macOS 12+ · Apple Silicon & Intel"
          badge="macOS"
          filename="HiralityAgent_mac.zip"
          steps={[
            "Download and unzip HiralityAgent_mac.zip",
            "Double-click HiralityAgent.app to launch",
            "Sign in with your HRMS email — auto-start is registered instantly",
          ]}
          onDownload={handleDownload}
          downloading={!!downloading["mac"]}
          done={!!done["mac"]}
        />
        <DownloadCard
          os="windows"
          icon={Monitor}
          iconColor={T.cyan}
          iconBg={`${T.cyan}1A`}
          title="Hirality Agent"
          subtitle="Windows 10/11 · 64-bit"
          badge="Windows"
          filename="HiralityAgent_windows.exe"
          steps={[
            "Download HiralityAgent_windows.exe",
            "Double-click to run — Windows may show SmartScreen, click 'Run anyway'",
            "Sign in with your HRMS email — auto-start is registered instantly",
          ]}
          onDownload={handleDownload}
          downloading={!!downloading["windows"]}
          done={!!done["windows"]}
        />
      </div>

      {/* Info card */}
      <div style={{
        padding:"18px 24px",
        background:`${T.cyan}0A`,
        border:`1px solid ${T.cyan}1A`,
        borderRadius:12,
        display:"flex", gap:14, alignItems:"flex-start",
      }}>
        <Shield size={18} style={{ color:T.cyan, flexShrink:0, marginTop:1 }}/>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:4 }}>
            Security & Privacy
          </div>
          <div style={{ fontSize:12.5, color:T.muted, lineHeight:1.7 }}>
            The agent only tracks <strong style={{color:T.text}}>active application names and idle time</strong>.
            It does not capture screenshots, keystrokes, or file contents.
            All data is encrypted in transit and stored on your HRMS server.
            Employees must be informed before installation as per company policy.
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse {
          0%,100% { box-shadow: 0 0 0 0 ${T.green}80; }
          50%      { box-shadow: 0 0 0 5px ${T.green}00; }
        }
      `}</style>
    </div>
  );
}
